import React from 'react';
import {Form, Formik} from 'formik';
import {useDispatch} from 'react-redux';

import {validateMobileOrEmail} from 'shared/utils';
import {saveActivity} from 'shared/portfolio/actions/activities';

import {sendReferrals} from 'portfolio/components/multiple-referral-modal/actions';

export interface LeadershipPosition {
  _id: string;
  description: string;
  schoolYear: string;
  title: string;
}

export interface Activity {
  _id: string;
  name: string;
  description: string;
  activityType: 'activity' | 'sport';
  where: 'school' | 'org';
  createdAt: number;
  updatedAt: number;
  schoolYears: string[];
  participation: 'minor' | 'medium' | 'major';
  organization: string | null;
  leadershipPositions: LeadershipPosition[];
  institutionId: string | null;
  referrals: string[];

  // not from the API, FE only
  editing?: boolean;
  new?: boolean;
  scrollTo?: boolean;
}

export type ActivityForm = Omit<
  Activity,
  'createdAt' | 'updatedAt' | 'institutionId' | 'participation'
> & {
  institution: {parentId: string; name: string; type: string};
  category?: string;
  participation: Activity['participation'] | null;
  activityType?: Activity['activityType'];
};

interface Props {
  initialValues: ActivityForm;
  children: React.ReactNode;
  onSuccess?: () => void;
}

const ActivitiesFormWrapper = ({initialValues, children, onSuccess}: Props) => {
  const dispatch = useDispatch();

  return (
    <Formik<ActivityForm>
      initialValues={initialValues}
      validate={(values) => {
        const errors: {
          name?: string;
          participation?: string;
          schoolYears?: string;
          leadershipPositions?: Record<string, Record<string, string>>;
          institution?: Record<string, string>;
          organization?: string;
          where?: string;
          referrals?: string[];
        } = {};

        if (!values.name) {
          errors.name = 'Activity name is required.';
        }

        if (!values.participation) {
          errors.participation = 'Participation is required.';
        }

        if (!values.schoolYears || values.schoolYears.length === 0) {
          errors.schoolYears = 'School year(s) is required.';
        }

        if (values.leadershipPositions.length) {
          values.leadershipPositions.forEach((leadership, i) => {
            const leadershipErrors: {title?: string; schoolYear?: string} = {};

            if (!leadership.title) {
              leadershipErrors.title = 'Position title is required.';
            }

            if (!leadership.schoolYear) {
              if (!values.schoolYears || values.schoolYears.length === 0) {
                leadershipErrors.schoolYear = 'Please choose years participated in activity above.';
              } else {
                leadershipErrors.schoolYear = 'School year is required.';
              }
            }

            if (Object.keys(leadershipErrors).length > 0) {
              if (!errors.leadershipPositions) {
                errors.leadershipPositions = {};
              }

              errors.leadershipPositions[i] = leadershipErrors;
            }
          });
        }

        if (!values.where) {
          errors.where = 'A location is required.';
        } else {
          if (values.where === 'school' && !values.institution?.parentId) {
            errors.institution = {
              name: 'A valid school name is required.',
            };
          } else if (values.where === 'org' && !values.organization) {
            errors.organization = 'Organization name is required.';
          }
        }

        values.referrals.forEach((referral, i) => {
          if (referral.length > 0 && !validateMobileOrEmail(referral)) {
            if (!errors.referrals) {
              errors.referrals = [];
            }

            errors.referrals[i] = 'Invalid email or mobile';
          }
        });

        return errors;
      }}
      onSubmit={async (values, formik) => {
        try {
          await dispatch(saveActivity(values));
          onSuccess?.();

          const referrals = values.referrals.filter((referral) => referral.length);

          if (referrals.length) {
            await sendReferrals(referrals);
          }
        } catch (errors: $TSFixMe) {
          Object.keys(errors).forEach((field) => {
            if (errors[field]) {
              formik.setFieldError(field, errors[field]);
            }
          });
        }
      }}
    >
      <Form autoComplete="off">{children}</Form>
    </Formik>
  );
};

export default ActivitiesFormWrapper;
